import { uniq } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, message, Modal, Radio } from 'antd';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ProductImages } from '../ProductImages';
import { SimpleInputNumber } from '../ToDoBundle/FormItem';
import { formatNumber } from 'utils/numberFormat';
import {
  calculateCurrency,
  CalculateRes,
  createCancel,
  getTradeEligibilityList,
  logisticsList,
  tradeList,
} from 'services/api/conversation';
import { useDebounceEffect } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import useModal from 'hooks/useModal';
import styled from '@emotion/styled';
import { DefaultButton } from 'components/LightConversation/components/DefaultButton';

const selectEnable = false;

function CancelOrderContent({
  cancelData,
  order,
  storeId,
  onUpdate,
  onClose,
}: {
  cancelData: Record<string, Record<string, string[]>>;
  order: Conversation.Trade.TradeWithLogistics;
  storeId: string | number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
  onClose: () => void;
}) {
  const { internalTradeId, externalTradeId, items, unit, platform, internalBuyerId } = order;

  const { t } = useTranslate();

  const [step, setStep] = useState<1 | 2>(1);

  const [selectedReason, setSelectedReason] = useState<string>();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const reasons = Object.keys(cancelData);

  const { reasonData, itemsCount, allChecked, indeterminate, ids, skusData } = useMemo(() => {
    if (!selectedReason) {
      return {
        reasonData: undefined,
        itemsCount: 0,
        allChecked: false,
        indeterminate: false,
        ids: [],
        skusData: [],
      };
    }

    const reasonData = cancelData[selectedReason];

    const itemsCount = Object.values(reasonData).reduce((res, ids) => res + ids.length, 0);

    const skuIds = Object.keys(reasonData);
    const ids = Object.values(reasonData).flat();
    const allChecked = ids.every((id) => selectedIds.includes(id));
    const indeterminate = !allChecked && ids.some((id) => selectedIds.includes(id));

    const skusData = skuIds.map((skuId) => {
      const item = items.find(({ externalSkuId }) => externalSkuId === skuId);
      const selected = selectedIds.filter((id) => reasonData[skuId].includes(id));
      return {
        skuId,
        item,
        selected,
      };
    });

    return {
      reasonData,
      itemsCount,
      allChecked,
      indeterminate,
      ids,
      skusData,
    };
  }, [cancelData, selectedReason, items, selectedIds]);

  const [loading, setLoading] = useState(false);
  const [calculateRes, setCalculateRes] = useState<CalculateRes>();

  const calculateRef = useRef(0);

  useDebounceEffect(() => {
    if (selectedReason && selectedIds.length) {
      setLoading(true);
      calculateRef.current += 1;
      const currentCalculate = calculateRef.current;
      calculateCurrency({
        internalTradeId,
        requestType: 'CANCEL',
        reasonName: selectedReason,
        orderLineItemIds: selectedIds,
      })
        .then((res) => {
          if (calculateRef.current === currentCalculate) {
            setLoading(false);
            setCalculateRes(res);
          }
        })
        .catch(() => {
          if (calculateRef.current === currentCalculate) {
            setLoading(false);
            setCalculateRes(undefined);
          }
        });
    } else {
      setCalculateRes(undefined);
    }
  }, [selectedReason, selectedIds]);

  useEffect(() => {
    return () => {
      setStep(1);
      setSelectedReason(undefined);
      setSelectedIds([]);
      setCalculateRes(undefined);
      calculateRef.current = 0;
    };
  }, [cancelData]);

  if (!reasons.length) {
    return <></>;
  }

  const step1 = (
    <Step1>
      <header>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
        <h2>{t('Select cancellation reason')}</h2>
        <div>{`${t('订单ID')}: ${externalTradeId}`}</div>
      </header>
      <div>
        <ul>
          {reasons.map((reason) => {
            const onChecked = () => {
              setSelectedReason(reason);
              setSelectedIds(
                !selectEnable
                  ? Object.values(cancelData[reason]).flat()
                  : Object.values(cancelData[reason]).reduce((res, ids) => res + ids.length, 0) ===
                    1
                  ? Object.values(cancelData[reason])[0]
                  : [],
              );
            };
            return (
              <li key={reason} onClick={onChecked}>
                <Radio checked={selectedReason === reason}>
                  <div>{t(`tiktok.returnReason.${reason}`)}</div>
                  {reason.includes('out_of_stock') && (
                    <span>{t('tiktok.returnReasonTips.out_of_stock')}</span>
                  )}
                  {reason.includes('reason_wrong_price') && (
                    <span>{t('tiktok.returnReasonTips.reason_wrong_price')}</span>
                  )}
                  {reason.includes('address') && (
                    <span>{t('tiktok.returnReasonTips.address')}</span>
                  )}
                  {reason.includes('reason_buyer_requested_cancellation') && (
                    <span>{t('tiktok.returnReasonTips.reason_buyer_requested_cancellation')}</span>
                  )}
                </Radio>
              </li>
            );
          })}
        </ul>
        <footer>
          <DefaultButton onClick={onClose}>{t('取消')}</DefaultButton>
          <Button type="primary" disabled={!selectedReason} onClick={() => setStep(2)}>
            {t('marketing.下一步')}
            <RightOutlined />
          </Button>
        </footer>
      </div>
    </Step1>
  );

  if (!selectedReason || !reasonData) {
    return <Content step={1}>{step1}</Content>;
  }

  const formatter = (num: number | string) => `${unit} ${formatNumber(num, '0,0.00')}`;

  const step2 = (
    <Step2>
      <header>
        <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setStep(1)} />
        <div>
          <span>{t('Select cancellation reason')}</span>
          <h2>{t('Edit cancellation details')}</h2>
        </div>
      </header>
      {selectedReason.includes('reason_buyer_requested_cancellation') && (
        <Alert
          closable
          banner
          showIcon
          type="info"
          message={
            <div>
              <div>
                {t('tiktok.returnReason.seller_cancel_paid_reason_buyer_requested_cancellation')}
              </div>
              <span>
                {t(
                  "Confirming the cancellation of the order will approve customer's cancelation request.",
                )}
              </span>
            </div>
          }
        />
      )}
      {selectedReason.includes('out_of_stock') && (
        <Alert
          closable
          banner
          showIcon
          type="info"
          message={
            <div>
              <div>
                {t('Item cancellation can only be applied to "Awaiting shipment" packages.')}
              </div>
            </div>
          }
        />
      )}
      <section>
        <div>
          <h3>{t('x个商品', { count: itemsCount })}</h3>
          {itemsCount > 1 && selectEnable && (
            <Checkbox
              checked={allChecked}
              indeterminate={indeterminate}
              onChange={({ target: { checked } }) => setSelectedIds(checked ? ids : [])}
            >
              {t('全选')}
            </Checkbox>
          )}
          <ul>
            {skusData.map(({ skuId, item, selected }) => {
              if (!item) {
                return null;
              }
              const { itemPic, itemName, sellerSku, unitPrice } = item;
              const count = reasonData[skuId].length;
              return (
                <li>
                  <ProductImages images={[itemPic]} platform={platform} />
                  <section>
                    <h4>{itemName}</h4>
                    <p>{`${t('SKU货号')}: ${sellerSku || '-'}`}</p>
                  </section>
                  <span>
                    <section>
                      {formatter(unitPrice)}
                      <span>x{count}</span>
                    </section>
                    {itemsCount > 1 && selectEnable && (
                      <SimpleInputNumber
                        min={0}
                        max={count}
                        value={selected.length}
                        onChange={(value) =>
                          setSelectedIds(
                            uniq([
                              ...selectedIds.filter((id) => !reasonData[skuId].includes(id)),
                              ...reasonData[skuId].slice(0, value),
                            ]),
                          )
                        }
                      />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <span>
          <h3>{t('Summary')}</h3>
          <ul>
            <li>
              <div>{`${t('Canceling items')}: ${selectedIds.length}`}</div>
              <span>{formatter(calculateRes?.refundTotal || 0)}</span>
            </li>
            {calculateRes && [
              <li>
                <div>{t('Product refund')}</div>
                <span>{formatter(calculateRes.refundSubtotal)}</span>
              </li>,
              <li>
                <div>{t('Shipping fee refund')}</div>
                <span>{formatter(calculateRes.refundShippingFee)}</span>
              </li>,
              <li>
                <div>{t('Tax refund')}</div>
                <span>{formatter(calculateRes.refundTax)}</span>
              </li>,
            ]}
          </ul>
          <div>{`${t('Cancellation reason')}: ${t(`tiktok.returnReason.${selectedReason}`)}`}</div>
          <Button
            block
            type="primary"
            disabled={!calculateRes || !selectedIds.length}
            loading={loading}
            onClick={() => {
              Modal.confirm({
                title: t('确认取消count件商品？', { count: selectedIds.length }),
                content: t('为避免损失，请勿在取消后发货或分配订单。'),
                onOk: () => {
                  setLoading(true);
                  createCancel({
                    internalTradeId,
                    orderLineItemIds: selectedIds,
                    cancelReason: selectedReason,
                  })
                    .then(() => {
                      message.success(t('操作成功'));
                      onClose();

                      setTimeout(() => {
                        tradeList({
                          page_no: 1,
                          page_size: 1,
                          internal_buyer_id: internalBuyerId,
                          internal_store_id: storeId,
                          external_trade_ids: externalTradeId,
                        })
                          .then((res) => {
                            const { tradeList: [trade] = [] } = res;

                            if (!trade) {
                              return;
                            }

                            return logisticsList({
                              internalStoreId: storeId,
                              internal_trade_ids: internalTradeId,
                              needPackageData: true,
                            }).then((res) => {
                              return getTradeEligibilityList({
                                internalTradeIds: [internalTradeId],
                              }).then(({ orderEligibilityList }) => {
                                return onUpdate({
                                  ...trade,
                                  logisticsList: res.data.data,
                                  eligibility: orderEligibilityList[0],
                                  ['_queryParams' as any]: (order as any)._queryParams,
                                });
                              });
                            });
                          })
                          .catch(() => {});
                      }, 2000);
                    })
                    .catch(() => {
                      message.error(t('translation.操作失败，请稍后再试'));
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                },
              });
            }}
          >
            {t('确认')}
          </Button>
        </span>
      </section>
    </Step2>
  );

  return (
    <Content step={step}>
      {step1}
      {step2}
    </Content>
  );
}

function CancelOrder({
  order,
  storeId,
  onUpdate,
}: {
  order: Conversation.Trade.TradeWithLogistics;
  storeId: string | number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
}) {
  const { t } = useTranslate();

  const { visible, show, hide } = useModal();

  const { eligibility: { skuEligibility } = {} } = order;

  const cancelData = useMemo(() => {
    return (skuEligibility || []).reduce<Record<string, Record<string, string[]>>>(
      (res, { skuId, lineItemEligibility }) => {
        return lineItemEligibility.reduce(
          (res, { requestType, orderLineItemIds, availableReasonNames }) => {
            if (requestType !== 'CANCEL') {
              return res;
            }
            return availableReasonNames.reduce((res, reason) => {
              if (!res[reason]) {
                res[reason] = {
                  [skuId]: orderLineItemIds,
                };
              } else if (!res[reason][skuId]) {
                res[reason][skuId] = orderLineItemIds;
              } else {
                res[reason][skuId] = uniq([...res[reason][skuId], ...orderLineItemIds]);
              }
              return res;
            }, res);
          },
          res,
        );
      },
      {},
    );
  }, [skuEligibility]);

  if (!Object.keys(cancelData).length) {
    return <></>;
  }

  const drawer = (
    <Drawer visible={visible} width={888} title={false} closable={false} onClose={hide}>
      <Container>
        <CancelOrderContent
          cancelData={cancelData}
          order={order}
          storeId={storeId}
          onUpdate={onUpdate}
          onClose={hide}
        />
      </Container>
    </Drawer>
  );

  const button = (
    <Button type="primary" size="small" onClick={show}>
      {t('取消订单')}
    </Button>
  );

  return (
    <>
      {button}
      {drawer}
    </>
  );
}

const Container = styled.div`
  margin: -24px;
  height: calc(100% + 48px);
  overflow: hidden;
`;

const Content = styled.div<{ step: 1 | 2 }>`
  display: flex;
  width: 200%;
  height: 100%;
  margin-left: ${({ step }) => (step === 2 ? '-100%' : '0')};
  overflow: hidden;
  transition: all 0.3s ease-out;
  > div {
    width: 50%;
    height: 100%;
  }
`;

const Step1 = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background: #f7f9fa;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    > * {
      margin-right: 8px;
    }
    > button {
      background: #f0f2f5;
    }
    > h2 {
      margin-bottom: 0;
    }
    > div {
      margin-top: 6px;
      font-size: 12px;
      color: #84868c;
    }
  }
  > div {
    flex: 0 1 auto;
    margin-top: 16px;
    padding: 16px;
    background: #fff;
    border-radius: 4px;
    overflow: auto;
    > ul {
      margin: 0;
      padding: 0;
      list-style: none;
      > li {
        margin-bottom: 16px;
        padding: 12px;
        border: solid 1px #f0f2f5;
        border-radius: 4px;
        cursor: pointer;
        > label > span > div {
          font-weight: 500;
          color: #232426;
        }
        > label > span > span {
          display: block;
          margin-top: 6px;
          font-weight: 400;
          color: #84868c;
        }
        .ant-radio-inner {
          margin-top: 0;
        }
      }
    }
    > footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      > button {
        margin-left: 16px;
      }
    }
  }
`;

const Step2 = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background: #f7f9fa;
  > header {
    flex: 0 0 auto;
    display: flex;
    margin-bottom: 16px;
    > * {
      margin-right: 8px;
    }
    > button {
      background: #f0f2f5;
    }
    > div {
      > span {
        font-size: 12px;
        color: #84868c;
      }
      > h2 {
        margin-bottom: 0;
      }
    }
  }
  > .ant-alert {
    flex: 0 0 auto;
    align-items: flex-start;
    margin-bottom: 16px;
    padding: 16px;
    background: #f0f2f5;
    border-radius: 4px;
    > span {
      margin-top: 4px;
      color: #84868c;
    }
    .ant-alert-message > div {
      > div {
        font-weight: 500;
        color: #232426;
      }
      > span {
        display: block;
        margin-top: 6px;
        color: #84868c;
      }
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    align-items: flex-start;
    overflow: hidden;
    > div {
      flex: 1 0 0;
      max-height: 100%;
      padding: 16px;
      background: #fff;
      border-radius: 4px;
      overflow: auto;
      overflow: auto;
      > label {
        margin-bottom: 12px;
      }
      > ul {
        margin: 0;
        padding: 0;
        list-style: none;
        > li {
          display: flex;
          padding: 12px;
          background: #f7f9fa;
          border-radius: 4px;
          &:not(:last-of-type) {
            margin-bottom: 16px;
          }
          > div {
            margin-right: 8px;
          }
          > section {
            flex: 1 0 0;
            overflow: hidden;
            > h4 {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              margin-bottom: 4px;
            }
            > p {
              margin-bottom: 0;
              font-size: 12px;
              color: #84868c;
            }
          }
          > span {
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-end;
            margin-left: 6px;
            > section {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
            }
          }
        }
      }
    }
    > span {
      flex: 0 0 auto;
      width: 252px;
      max-height: 100%;
      margin-left: 16px;
      padding: 14px 16px;
      border-radius: 4px;
      background: #fff;
      overflow: auto;
      > ul {
        margin: 0;
        padding: 0;
        list-style: none;
        > li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          &:first-of-type {
            font-weight: 500;
          }
          > div {
            flex: 1 0 0;
          }
          > span {
            flex: 0 0 auto;
          }
        }
      }
      > div {
        margin-bottom: 12px;
        color: #84868c;
      }
    }
  }
`;

export default CancelOrder;
